import React, { useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { useParams } from "react-router-dom";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { PluginKey } from "@tiptap/pm/state";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useApiQuery } from "../../../../utilities/apibelRequest";
import {
  TipTapContent,
  TipTapNode,
} from "../../../../shared/_core/comments.types";
import {
  createUserMentionExtension,
  createSectionMentionExtension,
} from "../utils/mentionEditorExtensions";
import {
  SectionsMentionIcon,
  UserMentionIcon,
} from "../../../../constants/commonIcons";

interface CommentDisplayProps {
  content: string;
  onContentChange?: (content: string) => void;
  isEditing?: boolean;
  onSectionLinkClick?: (sectionId: string) => void;
  editorRef?: React.MutableRefObject<Editor | null>;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
}
export const CommentDisplay: React.FC<CommentDisplayProps> = ({
  content,
  onContentChange,
  isEditing,
  onSectionLinkClick,
  editorRef,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const params = useParams<{ manualID: string }>();
  const { manualID } = params;

  const userQuery = useApiQuery("user/selectable", null);
  const manualSections = useApiQuery("manual/manySectionsForSelection", {
    query: "",
    manualID,
  });

  const validContent = useMemo((): TipTapContent => {
    if (!content) {
      return {
        type: "doc",
        content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }],
      };
    }

    try {
      return typeof content === "string" ? JSON.parse(content) : content;
    } catch {
      return {
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [{ type: "text", text: "Error loading content" }],
          },
        ],
      };
    }
  }, [content]);

  const extensions = useMemo(() => {
    const baseExtensions = [
      StarterKit,
      Placeholder.configure({
        placeholder: isEditing ? "Edit your comment..." : "",
      }),
      Mention.extend({ name: "userMention" }).configure({
        HTMLAttributes: { class: "mention" },
        suggestion: { pluginKey: new PluginKey("userMentionDisplay") },
        renderHTML({ node }) {
          return [
            "span",
            {
              class: "mention",
              "data-type": "mention",
              "data-id": node.attrs.id,
              style:
                "background-color: #e2e8f0; padding: 2px 4px; border-radius: 4px; color: #2d3748; font-weight: 500;",
            },
            `@${node.attrs.label || node.attrs.id}`,
          ];
        },
      }),
      Mention.extend({ name: "sectionMention" }).configure({
        HTMLAttributes: { class: "section-link" },
        suggestion: { pluginKey: new PluginKey("sectionMentionDisplay") },
        renderHTML({ node }) {
          const displayName =
            node.attrs.label ||
            `Section-${node.attrs.id?.slice(0, 8)}` ||
            "Unknown Section";
          return [
            "span",
            {
              class: "section-link",
              "data-type": "sectionLink",
              "data-id": node.attrs.id,
              style:
                "color: #3182ce; text-decoration: underline; cursor: pointer; background-color: #ebf8ff; padding: 2px 4px; border-radius: 4px; font-weight: 500;",
            },
            `/${displayName}`,
          ];
        },
      }),
    ];

    if (isEditing) {
      if (userQuery.isSuccess && userQuery.data) {
        baseExtensions.push(createUserMentionExtension(() => userQuery));
      }
      if (manualSections.isSuccess && manualSections.data) {
        baseExtensions.push(
          createSectionMentionExtension(() => manualSections),
        );
      }
    }

    return baseExtensions;
  }, [
    isEditing,
    userQuery.isSuccess,
    userQuery.data,
    manualSections.isSuccess,
    manualSections.data,
  ]);

  const editor = useEditor({
    extensions,
    content: validContent,
    editable: isEditing,
    onUpdate: ({ editor }) => {
      if (isEditing && onContentChange) {
        onContentChange(JSON.stringify(editor.getJSON()));
      }
    },
  });

  const insertMention = (char: string) => {
    if (!editor || !isEditing) return;

    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;

    const charBefore = $from.nodeBefore?.textContent?.slice(-1) || "";
    const needsSpace =
      charBefore &&
      charBefore !== " " &&
      charBefore !== "\n" &&
      charBefore !== char;

    if (charBefore === char) {
      editor.commands.deleteRange({ from: $from.pos - 1, to: $from.pos });
    }

    editor.commands.focus();
    editor.commands.insertContent(needsSpace ? ` ${char}` : char);
  };

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  useEffect(() => {
    if (!editor || isEditing || !onSectionLinkClick) return;

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const linkElement = target.classList.contains("section-link")
        ? target
        : (target.closest(".section-link") as HTMLElement);

      if (linkElement) {
        event.preventDefault();
        const sectionId = linkElement.getAttribute("data-id");
        if (sectionId) {
          onSectionLinkClick(sectionId);
        }
      }
    };

    editor.view.dom.addEventListener("click", handleClick);
    return () => editor.view.dom.removeEventListener("click", handleClick);
  }, [editor, isEditing, onSectionLinkClick]);

  if (!editor) return <div>Loading comment...</div>;

  return (
    <Box
      w="full"
      className={`manual-rte ${
        isEditing ? "comment-editing" : "comment-display"
      }`}
      minH={isEditing ? "120px" : "auto"}
      bg={isEditing ? "white" : "transparent"}
      border={isEditing ? "1px solid" : "none"}
      borderColor="gray.200"
      borderRadius="md"
      _focusWithin={
        isEditing ? { borderColor: "blue.400", boxShadow: "outline" } : {}
      }>
      <Box p={isEditing ? 3 : 0}>
        <Box
          sx={{
            ".ProseMirror": {
              minHeight: isEditing ? "100px" : "auto",
              padding: "0",
              outline: "none",
              border: "none",
              fontSize: "14px",
              lineHeight: "1.5",
              "&:focus": { outline: "none", boxShadow: "none" },
              p: { margin: "0 0 8px 0", "&:last-child": { marginBottom: "0" } },
            },
          }}>
          <EditorContent editor={editor} />
        </Box>

        {isEditing && (
          <VStack spacing={3} mt={3} w="full" pt={4}>
            <HStack w="full" justifyContent="space-between">
              <Flex gap={1}>
                <Tooltip label="Mention User (@)">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<UserMentionIcon />}
                    onClick={() => insertMention("@")}
                    isDisabled={!userQuery.isSuccess}
                    color="gray.600"
                    aria-label="Mention User"
                  />
                </Tooltip>
                <Tooltip label="Link Section (/)">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<SectionsMentionIcon />}
                    onClick={() => insertMention("/")}
                    isDisabled={!manualSections.isSuccess}
                    color="gray.600"
                    aria-label="Link Section"
                  />
                </Tooltip>
              </Flex>

              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="gray"
                  onClick={onCancel}
                  isLoading={isSaving}
                  isDisabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={onSave}
                  isLoading={isSaving}
                  isDisabled={isSaving}>
                  Save
                </Button>
              </HStack>
            </HStack>
          </VStack>
        )}
      </Box>
    </Box>
  );
};
interface TruncatedCommentDisplayProps {
  content: string;
  onSectionLinkClick?: (sectionId: string) => void;
  isEditing?: boolean;
}
export const TruncatedCommentDisplay: React.FC<
  TruncatedCommentDisplayProps
> = ({ content, onSectionLinkClick, isEditing }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTextContent = (contentData: string): string => {
    try {
      const parsed: TipTapContent =
        typeof contentData === "string" ? JSON.parse(contentData) : contentData;

      const extractText = (node: TipTapNode): string => {
        if (!node) return "";
        if (node.type === "text") return node.text || "";
        if (node.content && Array.isArray(node.content)) {
          return node.content.map(extractText).join("");
        }
        return "";
      };

      return extractText(parsed as TipTapNode);
    } catch {
      return "";
    }
  };

  const textContent = getTextContent(content);
  const shouldTruncate = textContent.length > 150;

  const createTruncatedContent = (
    originalContent: string,
    maxChars = 150,
  ): string => {
    try {
      const parsed =
        typeof originalContent === "string"
          ? JSON.parse(originalContent)
          : originalContent;
      let charCount = 0;

      const truncateNode = (node: TipTapNode): TipTapNode | null => {
        if (charCount >= maxChars) return null;

        if (node.type === "text" && node.text) {
          const availableChars = maxChars - charCount;
          if (node.text.length <= availableChars) {
            charCount += node.text.length;
            return node;
          }
          charCount = maxChars;
          return {
            ...node,
            text: `${node.text.substring(0, availableChars)}...`,
          };
        }

        if (node.content?.length) {
          const truncatedContent = node.content
            .map(truncateNode)
            .filter(Boolean) as TipTapNode[];
          return truncatedContent.length > 0
            ? { ...node, content: truncatedContent }
            : null;
        }

        return node;
      };

      const result = truncateNode(parsed);
      return result ? JSON.stringify(result) : originalContent;
    } catch {
      return originalContent;
    }
  };

  const displayContent = useMemo(() => {
    return shouldTruncate && !isExpanded
      ? createTruncatedContent(content)
      : content;
  }, [content, isExpanded, shouldTruncate]);

  return (
    <VStack align="flex-start" spacing={2} w="full">
      <CommentDisplay
        key={`comment-${isExpanded ? "expanded" : "truncated"}`}
        content={displayContent}
        onSectionLinkClick={onSectionLinkClick}
        isEditing={isEditing}
      />
      {shouldTruncate && (
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={() => setIsExpanded(!isExpanded)}
          alignSelf="flex-start"
          p={0}
          h="auto"
          minH="auto"
          fontWeight="normal">
          {isExpanded ? "Show less" : "Read more"}
        </Button>
      )}
    </VStack>
  );
};
